/**
 * NumberInput — controlled numeric input that behaves like humans expect:
 *
 * • Displays the parent's numeric value normally (e.g. "42", "0").
 * • On focus: if the value is 0, the display clears to "" so the caret
 *   sits in an empty box. Users no longer have to backspace the 0 first.
 * • While editing: the raw text (including "") is stored locally; the
 *   parent receives 0 for empty and parsed number otherwise. This means
 *   the box can visually stay empty while the parent state is a valid 0.
 * • On blur: if left empty, it renders as "0" again to keep the numeric
 *   contract with the parent, but the parent's value never changes to
 *   anything unexpected.
 *
 * Also inherits every prop from the underlying shadcn <Input/> so callers
 * can pass className, data-testid, min, max, placeholder, etc.
 */
import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

export function NumberInput({
  value,
  onChange,
  min,
  step = 1,
  disabled,
  className = "",
  ...rest
}) {
  // Local text state — decouples the DOM display from the numeric parent state.
  const [text, setText] = useState(String(value ?? 0));
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);

  // Sync from parent when value changes externally (e.g. reset, auto-fill).
  // Skipped while focused so the user's in-progress edit is not clobbered.
  useEffect(() => {
    if (!focused) setText(value === 0 || value == null ? "0" : String(value));
  }, [value, focused]);

  const commit = (raw) => {
    setText(raw);
    // Empty means "no value yet" — pass 0 to keep parent contract intact.
    if (raw === "" || raw === "-") {
      onChange?.(0);
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    if (min != null && parsed < min) {
      onChange?.(min);
      return;
    }
    onChange?.(parsed);
  };

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={text}
      disabled={disabled}
      className={className}
      onFocus={(e) => {
        setFocused(true);
        // Iter 42 UX: clicking a "0" field should feel like an empty box.
        if (text === "0" || text === "") {
          setText("");
        } else {
          // For non-zero values, select-all so typing replaces cleanly.
          requestAnimationFrame(() => {
            try { e.target.select(); } catch { /* ignore */ }
          });
        }
      }}
      onBlur={() => {
        setFocused(false);
        // Normalize display: empty → "0" to match the numeric parent state.
        if (text === "" || text === "-") setText("0");
      }}
      onChange={(e) => {
        const raw = e.target.value;
        // Only digits (+ optional leading minus if user wants it).
        if (raw === "" || /^-?\d*$/.test(raw)) {
          commit(raw);
        }
      }}
      {...rest}
    />
  );
}

export default NumberInput;
